'use client';

import { ChevronRight } from 'lucide-react';

import { useHorizontalScrollAffordance } from '@/hooks/useHorizontalScrollAffordance';

import type { DeviceFilter } from './types';

const FILTER_ITEMS: Array<{ key: DeviceFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'desktop', label: 'Desktop' },
  { key: 'mobile', label: 'Mobile' },
];

const TAB_CLASS =
  'flex flex-shrink-0 items-center gap-1 px-3 py-2.5 text-[11px] font-medium transition-colors border-b-2 -mb-px';
const COUNT_CLASS =
  'rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums';

interface SidebarDeviceFilterProps {
  value: DeviceFilter;
  counts: Record<DeviceFilter, number>;
  onChange: (filter: DeviceFilter) => void;
}

export function SidebarDeviceFilter({
  value,
  counts,
  onChange,
}: SidebarDeviceFilterProps) {
  const { scrollRef, canScrollRight, scrollRight } =
    useHorizontalScrollAffordance<HTMLDivElement>();

  return (
    <div className="relative border-b border-border">
      <div
        ref={scrollRef}
        className="flex items-center overflow-x-auto scroll-smooth px-3 pr-8"
        style={{ scrollbarWidth: 'none' }}
      >
        {FILTER_ITEMS.map(({ key, label }) => {
          const isActive = value === key;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              aria-pressed={isActive}
              className={`${TAB_CLASS} ${
                isActive
                  ? 'border-blue-700 text-blue-700'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
              {counts[key] > 0 && (
                <span
                  className={`${COUNT_CLASS} ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  ({counts[key]})
                </span>
              )}
            </button>
          );
        })}
      </div>
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll filters right"
          onClick={scrollRight}
          className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-8 bg-gradient-to-l from-background to-transparent text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
